import { ErrorBoundary } from '@src/components/ErrorBoundary';
import { MixpanelEventSource, MixpanelSourceContext } from '@talos/kyoko';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDealProductBuilderRoute } from '../../../../containers/Routes/routes';
import { useAppStateSelector } from '../../../../providers/AppStateProvider';
import type { DealProductBuilderPanelConfig } from '../../../../types/LayoutConfig';
import { DealProductBuilder } from '../../../DealProductBuilder';
import { Panel } from '../../../Layout/Components/Panel';
import { DEFAULT_LAYOUT_ID } from '../../Layouts/tokens';
import type { LayoutPanelProps } from '../../types';

export function DealProductBuilderPanel({ params, api }: LayoutPanelProps<DealProductBuilderPanelConfig>) {
  const { resolvedSymbol, bookingType, customTitle } = params;
  const navigate = useNavigate();
  const layoutId = useAppStateSelector(state => state.appLayout.currentLayout?.id);

  const handleUpdatePanelConfig = useCallback(
    (panelData: Partial<DealProductBuilderPanelConfig>, label: string) => {
      api.updateParameters(panelData);
      const newRoute = getDealProductBuilderRoute({
        bookingType: panelData.bookingType ?? bookingType,
        symbol: panelData.resolvedSymbol ?? resolvedSymbol,
        layoutId: layoutId ?? DEFAULT_LAYOUT_ID,
      });
      navigate(newRoute, { replace: true });
      if (!customTitle) {
        api.setTitle(label);
      }
    },
    [navigate, bookingType, resolvedSymbol, api, customTitle, layoutId]
  );

  return (
    <ErrorBoundary>
      <Panel panelApi={api} data-testid="deal-product-builder-panel">
        <MixpanelSourceContext.Provider value={MixpanelEventSource.DealProductBuilder}>
          <DealProductBuilder
            panelID={api.id}
            bookingType={bookingType}
            resolvedSymbol={resolvedSymbol}
            updateDealProductBuilderPanelData={handleUpdatePanelConfig}
          />
        </MixpanelSourceContext.Provider>
      </Panel>
    </ErrorBoundary>
  );
}
